import { PropsWithChildren, useState } from "react";
import BuildIcon from "@mui/icons-material/Build";
import CloseIcon from "@mui/icons-material/Close";
import InspectIcon from "@mui/icons-material/PageviewOutlined";
import InspectIconActive from "@mui/icons-material/PageviewTwoTone";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";

import { BaseButton } from "@/src/component/base/BaseButton";
import { ElementInspector } from "@/src/component/partial/__internal__/ElementInspector";

import { AnalyticsPanel, VariantTestingPanel } from "./component";

import styles from "./ToolKit.module.scss";
import theme from "@/styles/scss/theme.module.scss";
import ToolkitSelector from "./component/ToolkitSelector";

interface ToolKitProps {
  enable?: boolean;
}

function ToolKit({ children, enable }: PropsWithChildren<ToolKitProps>) {
  const [toolkitActive, setToolkitActive] = useState<"drawer" | "inline" | "float" | "off">("off");
  const [navigationOption, setNavigationOption] = useState<"analytics" | "variants">("variants");
  const [elementInspectorActive, setElementInspectorActive] = useState<boolean>(false);
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null);

  /**
   * when the user clicks a tab button switch what is render and update the url
   */
  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setNavigationOption(newValue as "analytics" | "variants");
  };

  const handleElementInspector = (on: boolean) => {
    setElementInspectorActive(on);
    if (!on) {
      setSelectedElement(null);
    }
  };

  if (!enable) {
    return <>{children}</>;
  }

  return (
    <>
      <div className={styles.launch}>
        {toolkitActive === "off" && (
          <BaseButton
            variant="contained"
            color="primary"
            onClick={() => setToolkitActive("inline")}
          >
            <BuildIcon />
          </BaseButton>
        )}
      </div>
      {toolkitActive === "off" && <>{children}</>}
      {toolkitActive == "inline" && (
        <div className={styles.container}>
          <div className={styles.content}>
            {children}
            <ElementInspector
              onSelectElement={setSelectedElement}
              isActive={elementInspectorActive}
              setIsActive={handleElementInspector}
            />
          </div>
          <div className={styles.toolkit}>
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <ToolkitSelector />
                <IconButton
                  onClick={() => handleElementInspector(!elementInspectorActive)}
                  size="small"
                >
                  {elementInspectorActive ? <InspectIconActive /> : <InspectIcon />}
                </IconButton>
                <IconButton
                  onClick={() => setToolkitActive("off")}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </div>
              <div className={styles.cardContent}>
                <Tabs
                  orientation="horizontal"
                  value={navigationOption}
                  onChange={handleChange}
                  aria-label="Navigation Options"
                  variant="scrollable"
                  scrollButtons="auto"
                  visibleScrollbar={false}
                  allowScrollButtonsMobile={true}
                  sx={{
                    ".MuiTabs-indicator": {
                      left: 0,
                      width: "4px",
                    },
                    ".MuiTabs-fixed": {
                      borderBottom: `1px solid ${theme.primaryMain}33`,
                    },
                    ".MuiTabs-flexContainer button": {
                      width: "50%",
                    },
                  }}
                >
                  <Tab
                    value="variants"
                    label={
                      <Typography
                        variant="body2"
                        fontFamily={"var(--roughwell)"}
                      >
                        A/B Testing
                      </Typography>
                    }
                  />
                  <Tab
                    value="analytics"
                    label={
                      <Typography
                        variant="body2"
                        fontFamily={"var(--roughwell)"}
                      >
                        Analytics
                      </Typography>
                    }
                  />
                </Tabs>
                {navigationOption === "analytics" && <AnalyticsPanel />}
                {navigationOption === "variants" && <VariantTestingPanel selectedElement={selectedElement} />}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/*
function ComponentSchemaForm({ componentSchema, componentData, pathToElement }: { componentSchema: any; componentData: any; pathToElement: string }) {
  const { attributes } = componentSchema;
  const [formData, setFormData] = useState<any>(componentData);

  const handleSave = async () => {
    await updateSectionData(formData, pathToElement);
  };

  return (
    <div className={styles.componentSchemaForm}>
      {Object.entries(attributes).map(([key, value]: any) => {
        if (value?.type !== "text") {
          return null;
        }

        return (
          <div key={key}>
            <TextField
              fullWidth
              label={key}
              value={formData[key]}
              onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
            />
          </div>
        );
      })}
      <BaseButton
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        onClick={handleSave}
      >
        Save
      </BaseButton>
    </div>
  );
}


async function getSectionData(sectionId: string) {
  try {
    const sectionData = await fetch(`${STRAPI_API_URL}/dynamic-page-sections/${sectionId}?populate=deep`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${STRAPI_API_TOKEN}`,
      },
    });
    const sectionDataJson = await sectionData.json();
    return sectionDataJson.data.attributes;
  } catch (error) {
    console.error("Error fetching section data:", error);
    return null;
  }
}

async function updateSectionData(data: any, pathToElement: string) {
  const requestBody = JSON.stringify({
    data: {
      variant_json_structure: data,
      variant_key: "b",
      content_variant_id: String(data.id),
      variant_type: pathToElement,
    },
  });

  try {
    await fetch(`${STRAPI_API_URL}/api/content-variants`, {
      method: "POST",
      body: requestBody,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${STRAPI_API_TOKEN}`,
      },
    });
    return { success: true };
  } catch (error) {
    console.error("Error fetching section data:", error);
    return { success: false };
  }
}

//TODO: get only specific component schema
async function getComponentSchema(componentType: string) {
  const componentsSchemaRequest = await fetch(`${COMMON_SERVICE_URL}/v1/components/flowhouse-cms`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${COMMON_SERVICE_TOKEN}`,
    },
  });
  const componentsSchema = await componentsSchemaRequest.json();
  return componentsSchema.payload.data[componentType];
}
*/

export default ToolKit;
